/**
 * Décoration native du tag `<context>` injecté dans les messages user via
 * `[innerHTML]="renderMarkdown(msg.content)"`.
 *
 * Le tag `<context>` n'est pas un nom de custom element valide (la spec impose
 * un trait d'union), on ne peut donc pas utiliser `customElements.define`.
 * À la place, un MutationObserver global détecte les `<context>` insérés dans le
 * DOM et les transforme en pastilles (icône Shoelace + titre lisible) — 100%
 * navigateur, sans passer par Angular.
 *
 * Format produit par le serveur (cf. agent.prompt-attachments.ts) :
 *   <context>
 *     <join type="pdf" slug="…" nom="…">…</join>
 *     <join type="email" id="…" mailbox="…">…</join>
 *     <join type="sharepoint" pageId="…" title="…" webUrl="…">…</join>
 *     <selection type="history" source="…">…texte…</selection>
 *   </context>
 */

import { ContextItem } from './kng-model.assistant';

/** Décode les entités XML basiques produites par escapeXmlAttribute (serveur). */
function decodeXmlEntities(value: string): string {
  return value
    .replace(/&quot;/g, '"')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');
}

/** Déduit la famille + l'icône d'un fichier à partir de son extension. */
function kindFromFilename(name: string): { kind: ContextItem['kind']; icon: string } {
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (ext === 'pdf') return { kind: 'pdf', icon: 'file-earmark-pdf' };
  if (['mp3', 'wav', 'm4a', 'ogg', 'aac', 'flac'].includes(ext)) return { kind: 'audio', icon: 'file-earmark-music' };
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) return { kind: 'image', icon: 'file-earmark-image' };
  if (['txt', 'md', 'doc', 'docx', 'odt', 'rtf'].includes(ext)) return { kind: 'text', icon: 'file-earmark-text' };
  return { kind: 'file', icon: 'paperclip' };
}

/** Transforme un slug/nom de fichier en titre lisible (sans extension ni séparateurs). */
function humanizeFilename(raw: string): string {
  const base = raw.replace(/\.[a-z0-9]{1,5}$/i, '');
  const words = base.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!words) return raw;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Construit un ContextItem (icône + titre lisible) depuis une balise du contexte. */
function toContextItem(el: Element): ContextItem | null {
  const tag = el.tagName.toLowerCase();

  if (tag === 'selection') {
    return {
      kind: 'selection',
      icon: 'quote',
      title: decodeXmlEntities(el.getAttribute('source') || '') || 'Extrait sélectionné'
    };
  }

  if (tag !== 'join') return null;

  const type = el.getAttribute('type');

  if (type === 'email') {
    const mailbox = decodeXmlEntities(el.getAttribute('mailbox') || '');
    return { kind: 'email', icon: 'envelope', title: mailbox ? `Email · ${mailbox}` : 'Email joint' };
  }

  if (type === 'sharepoint') {
    const webUrl = decodeXmlEntities(el.getAttribute('webUrl') || '');
    return {
      kind: 'sharepoint',
      icon: 'microsoft',
      title: decodeXmlEntities(el.getAttribute('title') || '') || 'Page SharePoint',
      ...(webUrl ? { href: webUrl } : {})
    };
  }

  //
  // type "pdf" (attachment file) : icône/titre déduits du nom de fichier sinon du slug
  const name = decodeXmlEntities(
    el.getAttribute('nom') || el.getAttribute('filename') || el.getAttribute('slug') || ''
  );
  const { kind, icon } = name ? kindFromFilename(name) : { kind: 'pdf' as const, icon: 'file-earmark-pdf' };
  return { kind, icon, title: name ? humanizeFilename(name) : 'Document joint' };
}

function escapeHtml(value: string): string {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}

/** Rend une pastille (lien si href, sinon span). */
function renderChip(item: ContextItem): string {
  const inner = `<sl-icon name="${item.icon}"></sl-icon><span class="context-chip__label">${escapeHtml(item.title)}</span>`;
  const attrs = `class="context-chip" data-kind="${item.kind}" title="${escapeHtml(item.title)}"`;
  return item.href
    ? `<a ${attrs} href="${escapeHtml(item.href)}" target="_blank" rel="noopener">${inner}</a>`
    : `<span ${attrs}>${inner}</span>`;
}

/** Décore un élément <context> (idempotent). */
function enhanceContext(el: HTMLElement): void {
  if (el.dataset['enhanced'] === 'true') return;
  el.dataset['enhanced'] = 'true';

  const items = Array.from(el.querySelectorAll('join, selection'))
    .map(toContextItem)
    .filter((i): i is ContextItem => i !== null);

  if (!items.length) {
    el.hidden = true;
    return;
  }

  el.innerHTML = items.map(renderChip).join('');
}

let registered = false;

/**
 * Active la décoration globale des `<context>`. À appeler une fois au bootstrap.
 */
export function registerContextElement(): void {
  if (registered || typeof window === 'undefined' || typeof document === 'undefined') return;
  registered = true;

  const scan = (node: ParentNode) => {
    node.querySelectorAll?.('context').forEach(el => enhanceContext(el as HTMLElement));
  };

  const observer = new MutationObserver(mutations => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach(node => {
        if (!(node instanceof HTMLElement)) return;
        if (node.tagName === 'CONTEXT') enhanceContext(node);
        else scan(node);
      });
    }
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });

  //
  // Décore les <context> déjà présents au moment de l'initialisation.
  scan(document);
}
